import { PatsnapCopilotIntentRendererRegistry } from '@analytics/patsnap-copilot/src/patsnap-copilot-renderer';
import type { PatsnapCopilotService, PatsnapCopilotUsage, SuggestionParams } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';
import { Message } from '@patsnap/tahiti-core/common/message';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { LanguageService } from '@analytics/shared/services/language-service';
import { GetSocketPath } from '@analytics/shared/services/socket-path';
import { PatsnapCopilotFeedBackService } from  '@analytics/shared/services/patsnap-copilot-feedback-service';
import { Site, SLD } from '@patsnap-utils/biz';
import type { WelComeMessage } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';

@injectable()
export abstract class AbstractAnalyticsCopilotServiceImpl implements PatsnapCopilotService {
    // @ts-ignore
    patsnapCopilotUsage: PatsnapCopilotUsage = {
        enabled: false,
        currentAmount: 0,
        maxAmount: 0,
        upgradeInvite: false,
    };

    @inject(Message)
    public readonly toast!: Message;
    @inject(PatsnapCopilotIntentRendererRegistry)
    private readonly _intentRendererRegistry!: PatsnapCopilotIntentRendererRegistry;
    @inject(LanguageService)
    private readonly _languageService!: LanguageService;
    @inject(GetSocketPath)
    private readonly _getSocketPath!: GetSocketPath;
    @inject(PatsnapCopilotFeedBackService)
    private readonly _feedbackService!: PatsnapCopilotFeedBackService;

    private urlencode(str: string) {
        return encodeURIComponent(`${str}`)
            .replace(/!/g, '%21')
            .replace(/'/g, '%27')
            .replace(/\(/g, '%28')
            .replace(/\)/g, '%29')
            .replace(/\*/g, '%2A')
            .replace(/%20/g, '+');
    }

    feedback(_data: { type: 'LIKE' | 'DISLIKE'; query: string; intent: string; product?: string | undefined; }) {
        const { product, ...data } = _data;
        return this._feedbackService.send({
            product: this.source || product || '',
            ...data,
        });
    }

    generateViewHref(context: { patentId: string; }) {
        if (!context.patentId) return 'javascript:;';
        return new Site({ sld: SLD.ANALYTICS, pathname: `patent-view?patentId=${context.patentId}` }).href;
    }

    generateCompanyHref(context: { id: string; }) {
        if (!context.id) return 'javascript:;';
        return new Site({ sld: SLD.ANALYTICS, pathname: `search/result?q=ANCS:${this.urlencode(`"${context.id}"`)}&efq=ANCS:${this.urlencode(`"${context.id}"`)}` }).href;
    }

    abstract source: string;

    abstract historyIdentifier(): string;

    abstract pageParamFunc(): Record<string, unknown>;

    actionCallback(type: 'JUMP_TO_SOURCE', data: Record<string, unknown>) {
        return false;
    }

    contextValidator(context: { source: string; params: any; }) {
        return true;
    }

    abstract welcomeMessages(): Promise<WelComeMessage[]>;

    abstract getSocketType(): string;

    getSocketPath() {
        return this._getSocketPath.getSocketPath({
            socketType: this.getSocketType(),
        });
    }

    getCurrentLanguage() {
        return this._languageService.getCurrentLanguage();
    }

    getSuggestion(data: SuggestionParams) {
        return Promise.resolve([]);
    }

    rendererFactory(intent: string) {
        const renderer = this._intentRendererRegistry.getRenderer({ intent });
        if (renderer) {
            return renderer;
        }
        return undefined;
    }

    generatePaperViewHref(context : { solutionId: string }) {
        if (!context.solutionId) return '';
        return new Site({ sld: SLD.HOME, pathname: `literature/#/abst?paperId=${context.solutionId}` }).href;
    }
}
